import type { PrivateKeyAccount } from 'viem/accounts';
import { create } from 'zustand';

import { toast } from '@/components/ui/toast';
import { useSignInDialogStore } from '@/features/auth/signInDialogStore';
// the hook module only (not the wallet barrel): it reaches back into this store, nothing else of the wallet feature
import { forgetV2Backend } from '@/features/wallet/hooks/v2BackendHook';
import {
    deleteBackup as deleteBackupRequest,
    fetchSnappyUser,
    getBackupBlob,
    isSnappyApiError,
    isSnappySessionExpiredError,
    putBackup,
    redeemSnappyAdmission,
    type SnappyBackupSummary,
    type SnappyPutBackupResult,
    type SnappyUser,
} from '@/lib/api/snappy';
import { loadEnginePresenter } from '@/lib/errors/engine';
import { queryClient, queryKeys } from '@/lib/query';
import { shortenAddress } from '@/lib/utils';
import type { BackupBlob } from '@snappy/core/lib/wallet/backup';

import {
    BLOB_COPY,
    DELETE_COPY,
    MAX_BACKUPS,
    PASSKEY_ERROR_COPY,
    PROTECT_COPY,
    RESTORE_COPY,
    SAVE_COPY,
    SETUP_COPY,
    TOAST_COPY,
    UNLOCK_COPY,
    VOUCHER_COPY,
    VOUCHER_ERROR_COPY,
} from './copy';
import type * as EngineModule from './engine';
import {
    peekHeldAddress,
    peekHeldProtection,
    wipeKeyRecordNow,
} from './keyRecordPeek';

/**
 * The Snappy Wallet in this browser (V2, plan 2026-10-04 §3.4): a React port of MoonHub's `stores/snappyWallet.ts`.
 *
 * The key (`snappy.browserWallet.key.v1`) belongs to the vendored keys shim; this store reads and writes it only through
 * the engine (`./engine`, loaded with a dynamic `import()` so V1 never carries it). The phrase never enters state:
 * `signer` carries a viem account, which signs without exposing it.
 *
 * Key vault (plan 2026-10-06-snappy-key-vault.md): at rest the key is ALWAYS an encrypted envelope (`v: 2`, a 6-digit
 * PIN or a passkey's PRF secret, `./vault/*`). Memory (§7 hardening): the shim keeps a viem account for this tab, never
 * the phrase or the key hex; a phrase exists only transiently, module-private here (the pending create / import /
 * restore wallet, a legacy record until it is sealed, the phrase a confirm opened for its continuation) and is dropped
 * once the account is derived and the envelope written.
 * - held: an envelope (or a legacy clear record) is stored; `address` is set from it without a secret. A held key is
 *   the identity (V2 stays active).
 * - sealed (`sealed`): held, and this tab has not opened it (every reload). Signer null; the banner and every surface
 *   that needs the key open the "unlock" view (`openUnlock`, `unlockWithPin`, `unlockWithPasskey`).
 * - unlocked: opened in this tab, and the backup rule below holds.
 * - legacy (`needsProtection`): a `v: 1` clear record. `boot()` loads it into memory and opens the "protect" view at
 *   once, not dismissable (the one exception to "boot never opens the dialog"); its signer is not published until
 *   the envelope is written, which removes the clear record in the same write. "Forget" is the other way out.
 * - The "protect" view (`setupPin`, `setupPasskey`, `cancelProtect`) runs right after a restore decrypts (after any
 *   held wallet is retired, before anything is written), in `finalizeKey` for a create or import (after the backup
 *   round-trips; `finalizeKey` awaits it), for a legacy record, and for a method switch. Cancelling it drops the key
 *   it was for. Nothing else creates a protect request, so `setupPin` / `setupPasskey` cannot re-seal a held wallet
 *   on their own.
 * - Confirm mode (§7 R3): a lock change (`changeProtection`, `changePin`), backing up the held wallet again
 *   (`backUpHeld`, `keepHeldWallet`) and the phrase reveal (`readRecoveryPhrase(credential)`) ask the CURRENT PIN or
 *   passkey, even in an unlocked tab (the unlock view, `unlockThen` set; it unlocks a sealed tab too), and work from
 *   the envelope that opens, never from memory.
 * - Wrong PINs (§7 R4, memory only): 3 free attempts, then each waits 2^(n-3) s (at most 30 s) before any scrypt
 *   (`pinRetryAt`); reset on success and on reload.
 * - Another tab's envelope of another wallet (or a wipe) drops this tab's memory record and retires the wallet; the
 *   same wallet re-sealed (another method) keeps it.
 *
 * Unlocked (`signer` set, V2 real mode) = a key is held AND (this account's backups list it, OR the account is unknown
 * because the social session or the Snappy servers are gone: trading never depends on them, §4.3). A held key that
 * this account does not back up is locked: `selectKeyMismatch` (the account has other backups: restore one or keep
 * this wallet by backing it up) or `selectHeldWithoutBackup` (the account has none: back it up).
 *
 * One dialog (`dialogOpen`, `view`) holds every step: "backups" (the account's backups with restore and delete, the
 * setup buttons, the expired state), "restore" (decrypt `selectedBackup`), "voucher" (the launch code a backup upload
 * asked for), "terms", "replaceHeld" (confirm replacing the held key), "create" (a new phrase), "import" (a typed
 * phrase), "backup" (password and recovery key, or a strong password alone, then the upload) and "done".
 *
 * - Backup required: a created or imported wallet's key is written only after Snappy confirms its backup (PUT, then the
 *   blob read back and compared field by field). An import skips the upload only once Snappy hands out a readable
 *   backup of that very wallet (GET blob). There is no skip.
 * - One wallet per browser: a create, or an import of another wallet, over a held key asks "replaceHeld" first; a
 *   restore of another wallet is confirmed inline in the backups list. The held key is replaced only when the new one
 *   is written (the record is overwritten in one write), so "Keep it", a cancel or a wrong password keeps it.
 * - Voucher: a PUT answered 403 `VOUCHER_REQUIRED` keeps the encrypted blob, shows the voucher view, redeems the code
 *   (factions admission) and retries the same PUT.
 * - Teardown: `forget()` (Sign out, "Forget this wallet on this browser") and a key wiped or replaced in another tab
 *   bump `teardownEpoch`; an async action that awaited across one drops its result.
 * - A wallet change always passes through a locked state that clears the V2 caches (`retireSigner`). Every path that
 *   can change the address this tab trades as (a restore or a create/import finalize over a held key, which a
 *   "replaceHeld" confirm leads to, `forget()`, another tab's key change, a `boot()` or a signer refresh that finds
 *   another key) first publishes `signer: null` (V2 demo mode, no V2 backend, no session key), removes the
 *   `queryKeys.speed.*` and `queryKeys.trades.*` caches, retires the previous wallet's memoized backend
 *   (`forgetV2Backend`: the instance refuses every call from then on) and yields so React renders the locked state and
 *   runs its effects (the trading store's real → demo reset); only then is the new key written and its signer
 *   published. No render and no effect ever pairs one wallet's balance, positions or trades with another's backend.
 * - `boot()` never opens the dialog (but for a legacy clear record's protect view): the flow store opens it once per
 *   tab session (`snappy.flow.v2DialogShown.v1`); for a sealed wallet that lands on the unlock view.
 *
 * Session signer (plan 2026-10-06-snappy-session-signer.md): a SharedWorker of this build that holds the key for the
 * browser session and signs only allow-listed operations (trading, the HyperCore deposit hop). Two tiers, both
 * "unlocked" for the banner and the trading flow:
 * - session (`sessionUnlocked`): on load the worker's signer holds exactly this browser's envelope (same SHA-256
 *   fingerprint), so the wallet unlocks with no prompt (`boot`, and another tab's `seeded` broadcast);
 * - this tab (`localUnlocked`): opened here with the PIN or passkey. Every tab-local unlock seeds the worker (unlock,
 *   protect after a create, import, restore or legacy record, a method switch re-seeds).
 * The signer published is the keys shim's two-tier account: what the worker refuses, and every typed data or message
 * (withdrawals, transfers, Relay deposits), asks for this tab's unlock once (`requestLocalUnlock`, the unlock view's
 * `sign` continuation). Sign out, forget, a wipe or another wallet forget the worker's signer; "Lock on all tabs"
 * (`lockEverywhere`) forgets it and seals every tab (BroadcastChannel `snappy-signer`). `sealed` means neither tier.
 * Locks win over unlocks in flight (review round 1): every lock, forget, sign out, wipe and received lock broadcast
 * bumps `lockGeneration`; every unlock and seed captures it before its first await and checks it after every await
 * and right before the seed (inside the session client too, just before the key is posted). A stale one drops what
 * it decrypted, publishes no signer, seeds nothing and leaves the dialog sealed. Every tab that hears a lock forgets
 * its OWN build's worker too (workers are build-bound: the sender's forget never reaches another build's).
 */

export type BrowserWalletIntent =
    'restore' | 'create' | 'manage' | 'import' | 'backup' | 'unlock';

export type BrowserWalletView =
    | 'backups'
    | 'restore'
    | 'voucher'
    | 'terms'
    | 'replaceHeld'
    | 'create'
    | 'import'
    | 'backup'
    | 'done'
    | 'protect'
    | 'unlock';

/** How the held key is encrypted at rest. */
export type BrowserWalletProtection = 'pin' | 'passkey';

/** What the protect step is for: a restored key, a new or imported one, a legacy clear record, a method switch. */
export type BrowserWalletProtectFor = 'restore' | 'setup' | 'legacy' | 'switch';

/**
 * Where an unlock continues: a method switch (the target), backing up the held wallet, or a signature only this tab
 * may make (`sign`: a withdrawal, a transfer, a Relay deposit). Set, the unlock view is in confirm mode ("Confirm it's
 * you") when the wallet is already unlocked (in this tab or through the session signer).
 */
export type BrowserWalletUnlockThen =
    BrowserWalletProtection | 'backup' | 'sign';

/** The current credential, for an operation that needs the phrase of an unlocked wallet. */
export type BrowserWalletCredential = { pin: string } | { passkey: true };

export type BrowserWalletPhraseResult = { words: string[] } | { error: string };

/** What the pending wallet is for: a new one, a typed phrase, or a new backup of the held one. */
export type BrowserWalletSetupKind = 'create' | 'import' | 'backup';

export type BrowserWalletUserError = 'expired' | 'network';

export interface BrowserWalletSigner {
    address: `0x${string}`;
    /**
     * The master key as a viem `PrivateKeyAccount` (`privateKeyToAccount(derivePrivateKeyHex(mnemonic))`). Typed
     * loosely so callers that only gate on the signer can fake one; read it with {@link signerAccount}.
     */
    account: unknown;
}

export type BrowserWalletBackup = SnappyBackupSummary;
export type BrowserWalletUser = SnappyUser;

export interface BrowserWalletBlobError {
    code: 'expired' | 'other';
    message: string;
}

export interface BrowserWalletState {
    /** The held key's address (an envelope or a legacy record is stored on this browser; sealed or not). */
    address: `0x${string}` | null;
    /**
     * The held key is an envelope neither this tab nor the session signer holds (a reload with no other tab of the app
     * open): signer null, `address` set.
     */
    sealed: boolean;
    /** The session signer holds the wallet for this tab (no prompt for allow-listed operations). */
    sessionUnlocked: boolean;
    /** This tab opened the wallet itself (PIN or passkey): nothing asks again in this tab. */
    localUnlocked: boolean;
    /** The held envelope's method. */
    protection: BrowserWalletProtection | null;
    /** A legacy clear record is held: the protect view is mandatory before its signer is published. */
    needsProtection: boolean;
    /** WebAuthn is there (known once the engine loaded): the protect view offers a passkey. */
    passkeyAvailable: boolean;
    /** What the open protect step is for (null: none). */
    protectFor: BrowserWalletProtectFor | null;
    /** The method preselected for a switch. */
    protectTarget: BrowserWalletProtection | null;
    /** Sealing (calibration and scrypt, or the passkey prompts). */
    protecting: boolean;
    protectError: string | null;
    /** The last passkey attempt failed: "Try passkey again" and the PIN form. */
    passkeyFailed: boolean;
    unlocking: boolean;
    unlockError: string | null;
    /** What the unlock view continues into once opened. */
    unlockThen: BrowserWalletUnlockThen | null;
    /** Wrong PINs: no PIN is tried before this time (ms epoch). Null: no wait. */
    pinRetryAt: number | null;
    /** Set when the held key is unlocked for this account: V2 real mode. */
    signer: BrowserWalletSigner | null;
    /** `GET /v2/snappy/user`; kept across a failed (non-401) refetch. */
    user: BrowserWalletUser | null;
    userLoaded: boolean;
    /** 'expired': the social session is gone (401); 'network': any other failed fetch. */
    userError: BrowserWalletUserError | null;
    loadingUser: boolean;
    /** `boot()` settled at least once: the banner stays quiet before. */
    booted: boolean;

    dialogOpen: boolean;
    view: BrowserWalletView;
    /** The dialog's on-open refetch is in flight: the backups view shows its skeleton. */
    refreshingOnOpen: boolean;
    /** The current view's error line (plain copy). */
    error: string | null;
    selectedBackup: BrowserWalletBackup | null;
    /** The encrypted backup being restored (not secret: it is what Snappy stores). */
    blob: BackupBlob | null;
    loadingBlob: boolean;
    blobError: BrowserWalletBlobError | null;
    restoring: boolean;
    restoreError: string | null;
    saving: boolean;
    redeeming: boolean;
    /** A launch code was accepted for the pending upload: the voucher view only retries now. */
    voucherAccepted: boolean;
    /** The address whose backup is being deleted. */
    deleting: string | null;
    termsAccepted: boolean;
    pendingSetup: BrowserWalletSetupKind | null;
    /** An import that must derive this wallet. */
    importTarget: `0x${string}` | null;
    /** The new wallet's 12 words, only while the create view shows them. */
    pendingWords: string[];
    pendingAddress: `0x${string}` | null;

    /** Read the key and the account; decide `signer`. Opens the dialog only for a legacy clear record (protect). */
    boot: () => Promise<void>;
    /**
     * Open the dialog: 'manage' the backups, 'restore' (the only backup's restore when no key is held), 'create',
     * 'import', 'backup' (the held wallet), 'unlock' (a sealed wallet). A sealed wallet unlocks first.
     */
    open: (intent: BrowserWalletIntent) => void;
    /** The unlock view of a sealed wallet (the protect view of a legacy one). */
    openUnlock: () => void;
    unlockWithPin: (pin: string) => Promise<boolean>;
    unlockWithPasskey: () => Promise<boolean>;
    /** Seal the key the protect step is for under a 6-digit PIN. */
    setupPin: (pin: string) => Promise<boolean>;
    /** Seal the key the protect step is for under a new passkey (WebAuthn PRF). */
    setupPasskey: () => Promise<boolean>;
    /** Leave the protect step, dropping the key it was for (not for a legacy record). */
    cancelProtect: () => void;
    /** Re-seal the held wallet with `target`: confirm mode (the current PIN or passkey) first. */
    changeProtection: (target: BrowserWalletProtection) => Promise<void>;
    changePin: () => Promise<void>;
    /** Re-read the account (the backups view's Retry). */
    refreshUser: () => Promise<void>;
    /** "Not now", "Skip restore", X, overlay or Escape. */
    dismiss: () => void;
    showBackups: () => void;
    selectBackup: (address: string) => Promise<void>;
    loadBlob: (address: string) => Promise<void>;
    restore: (
        password: string,
        recoveryKey: string,
    ) => Promise<`0x${string}` | null>;
    startSetup: (
        kind: 'create' | 'import',
        options?: { target?: `0x${string}` | null },
    ) => Promise<void>;
    /** Back up the held wallet again: confirm mode first, then from the phrase that confirm opened. */
    backUpHeld: () => Promise<void>;
    /** keyMismatch "Keep this wallet": back it up to this account, which unlocks it. */
    keepHeldWallet: () => Promise<void>;
    confirmReplaceHeld: () => Promise<void>;
    cancelReplaceHeld: () => void;
    acceptTerms: () => void;
    createWallet: () => Promise<boolean>;
    continueToBackup: () => void;
    importPhrase: (phrase: string) => Promise<boolean>;
    /** `recoveryKey` null saves a password-only backup (blob v4; the form gates the stronger policy). */
    saveBackup: (
        password: string,
        recoveryKey: string | null,
    ) => Promise<boolean>;
    redeemVoucher: (code: string) => Promise<boolean>;
    /** The voucher view's "Try again" once a code was accepted. */
    retrySave: () => Promise<boolean>;
    cancelSetup: () => void;
    deleteBackup: (address: string) => Promise<boolean>;
    clearError: () => void;
    /** Close the dialog and open the sign-in dialog. */
    signInAgain: () => void;
    /**
     * The held wallet's recovery phrase as words, opened from its envelope by the CURRENT PIN or passkey (never from
     * memory), for a reveal the caller shows and drops (never put it in state or a log). Wrong PINs count towards the
     * throttle. `{ error }` carries plain copy.
     */
    readRecoveryPhrase: (
        credential: BrowserWalletCredential,
    ) => Promise<BrowserWalletPhraseResult>;
    /**
     * Wipe this browser's key and the signer, and the V2 caches with them (Sign out, "Forget this wallet on this
     * browser"). Synchronous. Backups stay.
     */
    forget: () => void;
    /** "Lock on all tabs": forgets the session signer's key and seals this tab and every other open tab. */
    lockEverywhere: () => Promise<void>;
    /**
     * Opens this tab's unlock for a signature only this tab may make (the two-tier account asks it): resolves once this
     * tab holds its own signer (at once when it already does), rejects with the locked error when the user closes it.
     * One prompt per tab: concurrent requests share it.
     */
    requestLocalUnlock: () => Promise<void>;
}

const TERMS_ACCEPTED_KEY = 'snappy.browserWallet.termsAcceptedAt.v1';

/** Views that hold a pending wallet the dialog only drops after a confirm. */
const SETUP_VIEWS: ReadonlySet<BrowserWalletView> = new Set<BrowserWalletView>([
    'create',
    'import',
    'backup',
]);

type Engine = typeof EngineModule;
type Hex = `0x${string}`;

function sameAddress(a: string, b: string): boolean {
    return a.toLowerCase() === b.toLowerCase();
}

function isListed(user: BrowserWalletUser | null, wallet: string): boolean {
    return !!user?.backups.some((backup) =>
        sameAddress(backup.address, wallet),
    );
}

/**
 * scrypt (noble's scryptAsync) yields only microtasks, so the page never repaints while it runs: a busy flag set right
 * before it would not be on screen. One painted frame first.
 */
async function yieldToPaint(): Promise<void> {
    return new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => setTimeout(resolve, 0));
        } else {
            setTimeout(resolve, 0);
        }
    });
}

/**
 * After a retire published the locked state: React renders it and runs its effects (a store update made outside a
 * React event commits in a microtask; the trading store's real → demo reset runs in those effects) before the next
 * wallet's key is written and its signer published. A painted frame where the page is visible; one macrotask in a
 * hidden tab, which gets no frame (another tab's key change lands there).
 */
async function yieldToLockedRender(): Promise<void> {
    if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
    ) {
        return new Promise((resolve) => {
            setTimeout(resolve, 0);
        });
    }
    return yieldToPaint();
}

function readTermsAccepted(): boolean {
    try {
        return Number.isFinite(
            Number.parseInt(localStorage.getItem(TERMS_ACCEPTED_KEY) ?? '', 10),
        );
    } catch {
        return false;
    }
}

function writeTermsAccepted(): void {
    try {
        localStorage.setItem(TERMS_ACCEPTED_KEY, String(Date.now()));
    } catch {
        // accepted for this page's life only.
    }
}

/** A log tag for a failed call: the error's name, HTTP status and API code, never its text. */
function errorTag(error: unknown): string {
    const name = error instanceof Error ? error.name : typeof error;
    if (isSnappyApiError(error)) {
        return [name, error.status, error.code].filter(Boolean).join(' ');
    }
    return name;
}

/** `error` may already be a tag (an error's name): the text of an error is never logged. */
function logFailure(where: string, error: unknown): void {
    const tag = typeof error === 'string' ? error : errorTag(error);
    console.warn(`[browserWallet] ${where} failed: ${tag}`);
}

function errorName(error: unknown): string | null {
    return typeof error === 'object' && error !== null
        ? (((error as { name?: unknown }).name as string | null) ?? null)
        : null;
}

const RESTORE_ERROR_BY_NAME: Readonly<Record<string, string>> = {
    RecoveryKeyFormatError: RESTORE_COPY.badRecoveryKey,
    RecoveryKeyRequiredError: RESTORE_COPY.needsRecoveryKey,
    BackupFormatError: RESTORE_COPY.badFormat,
};

/** Plain copy for a failed restore; never the raw error text. */
function restoreErrorCopy(error: unknown, needsRecoveryKey: boolean): string {
    const name = errorName(error);
    if (name === 'BackupPasswordError') {
        return needsRecoveryKey
            ? RESTORE_COPY.wrongSecret
            : RESTORE_COPY.wrongPassword;
    }
    return (name && RESTORE_ERROR_BY_NAME[name]) ?? RESTORE_COPY.failed;
}

const PIN_PATTERN = /^[0-9]{6}$/;

/** Plain copy for a failed passkey step: the error's name decides, never its text. */
function passkeyErrorCopy(error: unknown): string {
    const name = errorName(error);
    if (name === 'NotAllowedError' || name === 'AbortError') {
        return PASSKEY_ERROR_COPY.cancelled;
    }
    if (name === 'PasskeyPrfUnsupportedError') {
        return PASSKEY_ERROR_COPY.unsupported;
    }
    return PASSKEY_ERROR_COPY.failed;
}

/** Plain copy for a failed unlock. */
function unlockErrorCopy(
    error: unknown,
    method: BrowserWalletProtection,
): string {
    const name = errorName(error);
    if (name === 'VaultPinError') return UNLOCK_COPY.wrongPin;
    if (name === 'VaultFormatError') return UNLOCK_COPY.unreadable;
    // the setup copy's "use a PIN" does not apply: a passkey envelope has no PIN, and only its own credential opens it
    if (name === 'PasskeyPrfUnsupportedError') {
        return UNLOCK_COPY.passkeyUnsupported;
    }
    if (method === 'passkey') return passkeyErrorCopy(error);
    return UNLOCK_COPY.failed;
}

const SAVED_TOAST: Readonly<Record<BrowserWalletSetupKind, string>> = {
    create: TOAST_COPY.created,
    import: TOAST_COPY.imported,
    backup: TOAST_COPY.backedUp,
};

// ── module-private: never in state ──────────────────────────────────────────

let enginePromise: Promise<Engine> | null = null;
let engineModule: Engine | null = null;
let engineReady: Promise<void> | null = null;
let unbindWalletStore: (() => void) | null = null;
let unsubscribeKeyRecord: (() => void) | null = null;
// set while this store writes or wipes the record itself, so the record subscription only reacts to other tabs
let ownKeyWrite = false;
// bumped by every teardown (forget, a key wiped or replaced elsewhere, a restore replacing a held key of another
// wallet); synchronous, so an action started right after a teardown is not voided by it
let teardownEpoch = 0;
// bumped by every lock: "Lock on all tabs", a lock heard from another tab, a forget (Sign out included) and a wipe or
// replace in another tab. An unlock or a seed that began before it drops its result (review round 1, finding 1).
let lockGeneration = 0;
let bootInflight: Promise<void> | null = null;
let userInflight: Promise<void> | null = null;
let blobRequest = 0;
// the wallet being created, imported or backed up again, until its backup is confirmed
let pending: { mnemonic: string; address: Hex } | null = null;
// its encrypted backup, kept while a launch code is asked for so the same upload can be retried
let pendingBlob: BackupBlob | null = null;
let replaceHeldConfirmed = false;
// the wallet the V2 caches and the memoized V2 backend may still describe: the last signer published, until a retire
let servedAddress: Hex | null = null;
// the signatures waiting for this tab's unlock (`requestLocalUnlock`)
let signWaiters: { resolve: () => void; reject: (error: unknown) => void }[] =
    [];
// the `snappy-signer` BroadcastChannel this tab listens on (lock on all tabs, a signer seeded elsewhere)
let sessionChannel: BroadcastChannel | null = null;

/** A wallet's phrase and public side, for the moment it takes to seal it or to seed the session signer with it. */
interface SeedKey {
    mnemonic: string;
    address: Hex;
    userId: string | null;
}

/** The key the protect step seals: a restored, new, imported or legacy phrase, or the held one for a switch. */
interface ProtectPending {
    mnemonic: string;
    address: Hex;
    userId: string | null;
    for: BrowserWalletProtectFor;
    /** The teardown epoch it began in: a teardown since drops it. */
    epoch: number;
}
// never in state: the phrase waiting for its PIN or passkey
let protectPending: ProtectPending | null = null;
// the finalizeKey waiting on a setup's protect step: true once the envelope is written
let protectWaiter: ((done: boolean) => void) | null = null;
// the wrong-PIN throttle (§7 R4), memory only: a reload starts over
const PIN_FREE_ATTEMPTS = 3;
const PIN_MAX_WAIT_S = 30;
let pinFailures = 0;
let pinRetryAtMs = 0;

/** Why a signer is retired; only a forget skips the yield (nothing is published after it, and it must stay sync). */
type RetireReason = 'restore' | 'replace' | 'switch' | 'foreign' | 'forget';

async function loadEngine(): Promise<Engine> {
    if (!enginePromise) {
        // with the vendored error presenter, which the web presenter hands the engine's errors to
        enginePromise = Promise.all([
            import('./engine'),
            loadEnginePresenter(),
        ]).then(
            ([engine]) => {
                engineModule = engine;
                unsubscribeKeyRecord ??= engine.subscribeKeyRecord((change) => {
                    if (change === 'session') {
                        // the session signer attached or went away: the same wallet, another tier
                        onSessionChange(engine);
                    } else if (!ownKeyWrite) {
                        onForeignKeyChange(engine);
                    }
                });
                // the two-tier account asks for this tab's unlock through the store's unlock view
                engine.setLocalUnlockRequester(async () =>
                    useBrowserWalletStore.getState().requestLocalUnlock(),
                );
                listenToSessionChannel(engine);
                useBrowserWalletStore.setState({
                    passkeyAvailable: engine.passkeysAvailable(),
                });
                return engine;
            },
            (error: unknown) => {
                // a chunk that failed to load (offline, a deploy replaced it): the next call tries again
                enginePromise = null;
                throw error;
            },
        );
    }
    return enginePromise;
}

/** Binds the vendored wallet store to the record and restores the nonce mark, once, before the first signature. */
async function ensureEngineReady(engine: Engine): Promise<void> {
    if (!engineReady) {
        unbindWalletStore = engine.bindWalletStoreToKeyRecord();
        engineReady = engine.restoreNonceMark();
    }
    return engineReady;
}

/** A key write or unlock of this store's own: the record subscription only reacts to other tabs. */
function ownWrite(write: () => void): void {
    ownKeyWrite = true;
    try {
        write();
    } finally {
        ownKeyWrite = false;
    }
}

type StoredView = ReturnType<Engine['readKeyEnvelope']>;

/** The address the stored key holds (in clear in both shapes), or null. */
function heldAddressOf(stored: StoredView): Hex | null {
    if (stored === null) return null;
    return 'legacy' in stored ? stored.record.address : stored.address;
}

function wipeKey(): void {
    ownKeyWrite = true;
    try {
        if (engineModule) {
            engineModule.clearKeyRecord();
        } else {
            // the engine never loaded on this page, so nothing here derived anything from the record
            wipeKeyRecordNow();
        }
    } finally {
        ownKeyWrite = false;
    }
}

/** The encrypted backup of the pending wallet, kept for a retry after a launch code. */
function rememberBlob(blob: BackupBlob): void {
    pendingBlob = blob;
}

function discardPending(): void {
    pending = null;
    pendingBlob = null;
}

/** Every signature waiting for this tab's unlock fails (the unlock view closed, a lock, a forget). */
function rejectSignWaiters(): void {
    const waiters = signWaiters;
    signWaiters = [];
    if (waiters.length === 0) return;
    const engine = engineModule;
    for (const waiter of waiters) {
        waiter.reject(
            engine ? new engine.WalletLockedError() : new Error('Locked'),
        );
    }
}

/** This tab holds its own signer now: every waiting signature goes on. */
function resolveSignWaiters(): void {
    const waiters = signWaiters;
    signWaiters = [];
    for (const waiter of waiters) waiter.resolve();
}

let onSessionChange: (engine: Engine) => void = () => undefined;
let onSessionMessage: (engine: Engine, message: unknown) => void = () =>
    undefined;

function listenToSessionChannel(engine: Engine): void {
    if (sessionChannel !== null || typeof BroadcastChannel === 'undefined') {
        return;
    }
    try {
        sessionChannel = new BroadcastChannel(engine.SESSION_CHANNEL_NAME);
        sessionChannel.onmessage = (event: MessageEvent) =>
            onSessionMessage(engine, event.data);
    } catch {
        sessionChannel = null;
    }
}

/** Test seam: forget every module-private bit (the engine stays loaded). */
export function resetBrowserWalletModuleForTests(): void {
    teardownEpoch += 1;
    lockGeneration += 1;
    rejectSignWaiters();
    protectPending = null;
    protectWaiter?.(false);
    protectWaiter = null;
    pinFailures = 0;
    pinRetryAtMs = 0;
    bootInflight = null;
    userInflight = null;
    blobRequest = 0;
    discardPending();
    replaceHeldConfirmed = false;
    servedAddress = null;
}

/** The viem account of an unlocked signer. */
export function signerAccount(signer: BrowserWalletSigner): PrivateKeyAccount {
    return signer.account as PrivateKeyAccount;
}

/** The held key is not one of this account's backups, and the account has others. */
export function selectKeyMismatch(state: BrowserWalletState): boolean {
    return (
        state.address !== null &&
        state.userLoaded &&
        (state.user?.backups.length ?? 0) > 0 &&
        !isListed(state.user, state.address)
    );
}

/** The held key has no backup on this account, which holds none. */
export function selectHeldWithoutBackup(state: BrowserWalletState): boolean {
    return (
        state.address !== null &&
        state.userLoaded &&
        (state.user?.backups.length ?? 0) === 0
    );
}

/** The dialog must not close on overlay or Escape (a pending wallet or a busy step). */
export function selectDialogLocked(state: BrowserWalletState): boolean {
    return (
        state.restoring ||
        state.saving ||
        state.redeeming ||
        state.protecting ||
        state.unlocking ||
        SETUP_VIEWS.has(state.view) ||
        // a key waits for its PIN or passkey (a legacy one has no other way out than a forget)
        state.view === 'protect' ||
        // the launch code an upload asked for: the pending wallet waits behind it
        (state.view === 'voucher' && state.pendingAddress !== null)
    );
}

let onForeignKeyChange: (engine: Engine) => void = () => undefined;

export const useBrowserWalletStore = create<BrowserWalletState>((set, get) => {
    const setPending = (mnemonic: string, address: Hex) => {
        pending = { mnemonic, address };
        pendingBlob = null;
        set({ pendingAddress: address });
    };

    const endSetup = () => {
        discardPending();
        replaceHeldConfirmed = false;
        set({
            pendingSetup: null,
            importTarget: null,
            pendingWords: [],
            pendingAddress: null,
            voucherAccepted: false,
        });
    };

    const onPendingView = (): boolean => {
        const { view } = get();
        return (
            SETUP_VIEWS.has(view) ||
            (view === 'voucher' && pending !== null) ||
            (view === 'protect' && protectPending !== null)
        );
    };

    /** What the stored key and this tab's memory say: the held address and how it is (un)locked. */
    const lockFields = (engine: Engine) => {
        const stored = engine.readKeyEnvelope();
        const legacy = stored !== null && 'legacy' in stored;
        return {
            address: heldAddressOf(stored),
            // neither this tab nor the session signer holds it (readKeyRecord answers either)
            sealed:
                stored !== null && !legacy && engine.readKeyRecord() === null,
            protection:
                stored !== null && !('legacy' in stored) ? stored.method : null,
            needsProtection: legacy,
            sessionUnlocked: !legacy && engine.hasSessionTier(),
            localUnlocked: !legacy && engine.hasLocalSigner(),
        };
    };

    /**
     * A restore or a setup waiting on its protect step replaces `held`: the held wallet was retired for it, and its
     * signer stays unpublished until the step ends (a cancel decides it again).
     */
    const holdForProtect = (held: Hex | null): boolean =>
        protectPending !== null &&
        (protectPending.for === 'restore' || protectPending.for === 'setup') &&
        (held === null || !sameAddress(held, protectPending.address));

    /** Ends the protect step (the key it was for leaves memory); a waiting finalizeKey learns whether it sealed. */
    const endProtect = (done: boolean) => {
        protectPending = null;
        const waiter = protectWaiter;
        protectWaiter = null;
        set({
            protectFor: null,
            protectTarget: null,
            protecting: false,
            protectError: null,
            passkeyFailed: false,
        });
        waiter?.(done);
    };

    /** A protect step overtaken by a wallet change: dropped, back to the backups view. */
    const abandonProtect = () => {
        if (!protectPending) return;
        const kind = protectPending.for;
        endProtect(false);
        if (kind === 'setup') endSetup();
        if (get().view === 'protect') set({ view: 'backups' });
    };

    const beginProtect = (
        key: { mnemonic: string; address: Hex; userId: string | null },
        kind: BrowserWalletProtectFor,
        target: BrowserWalletProtection | null = null,
    ) => {
        if (protectPending) endProtect(false);
        protectPending = { ...key, for: kind, epoch: teardownEpoch };
        set({
            dialogOpen: true,
            view: 'protect',
            protectFor: kind,
            protectTarget: target,
            protecting: false,
            protectError: null,
            passkeyFailed: false,
            error: null,
        });
    };

    /** The unlock view, continuing into `then` once opened. */
    const showUnlock = (then: BrowserWalletUnlockThen | null = null) => {
        // another purpose takes the view: a signature waiting on it fails (its caller shows the locked error)
        if (then !== 'sign') rejectSignWaiters();
        set({
            dialogOpen: true,
            view: 'unlock',
            unlockThen: then,
            unlockError: null,
            error: null,
        });
    };

    /** A 401: drop any pending wallet and show the backups view's expired state. */
    const expireSession = (copy: string | null = null) => {
        endSetup();
        set({
            user: null,
            userLoaded: false,
            userError: 'expired',
            error: copy,
            view: 'backups',
        });
    };

    /** The wallet the V2 caches may describe: the last signer published, or the one in state. */
    const servedWallet = (): Hex | null =>
        servedAddress ?? get().signer?.address ?? null;

    /** `next` (null: no key) is not the wallet the V2 caches and the V2 backend describe. */
    const wouldChangeWallet = (next: string | null): boolean => {
        const served = servedWallet();
        return served !== null && (next === null || !sameAddress(served, next));
    };

    /** Writing `next` over the record holding `held` replaces a wallet (the one this tab trades as, or a held one). */
    const replacesWallet = (next: string, held: string | null): boolean =>
        wouldChangeWallet(next) || (held !== null && !sameAddress(held, next));

    /**
     * The one way out of an unlocked wallet when the address this tab trades as may change (the header's rule):
     *
     * 1. `teardownEpoch` is bumped, so whatever was keyed to the previous wallet (a read, a signer refresh, a blob
     *    load) drops its result;
     * 2. `signer: null` is published: V2 demo mode, `useV2Backend` null, no session key;
     * 3. the V2 server caches (`queryKeys.speed.*`, `queryKeys.trades.*`) are removed, in-flight reads cancelled with
     *    them: the previous wallet's balance, positions and trades are gone before anything else can render;
     * 4. the previous wallet's memoized backend is retired (`forgetV2Backend`): a closure still holding it can neither
     *    act nor read into the caches again;
     * 5. one yield (`yieldToLockedRender`) so React renders the locked state and runs its effects, among them the
     *    trading store's real → demo reset of every per-coin slot. Not for a forget, which must stay synchronous and
     *    publishes nothing after.
     *
     * Only then does the caller write the new key and publish its signer (`address` of a new key is set after this
     * too). Resolves with the epoch the retire left: a caller that finds another one after the await was overtaken
     * by a teardown (a forget during the yield) and drops its result. Reached only in V2 code paths; V1 never holds a
     * signer, and its Sign out clears the whole query cache before `forget()` anyway.
     */
    const retireSigner = async (reason: RetireReason): Promise<number> => {
        const previous = servedWallet();
        teardownEpoch += 1;
        const epoch = teardownEpoch;
        servedAddress = null;
        if (get().signer !== null) set({ signer: null });
        // the session signer of the retired wallet: only if it still holds it (another tab may have seeded the next)
        if (engineModule && reason !== 'forget') {
            void engineModule.forgetSession({ onlyAttached: true });
        }
        queryClient.removeQueries({ queryKey: queryKeys.speed.all });
        queryClient.removeQueries({ queryKey: queryKeys.trades.all });
        if (previous !== null) forgetV2Backend(previous);
        if (reason !== 'forget') await yieldToLockedRender();
        return epoch;
    };

    /**
     * Seeds the session signer with the wallet this tab just opened with its PIN or passkey (or sealed), so the app's
     * other tabs, and this one after a reload while another is open, need no prompt for allow-listed operations. In
     * the background: the attach that follows refreshes the signer (`onSessionChange`). The phrase is turned into the
     * key at once (synchronously) and not kept.
     */
    const seedSession = (engine: Engine, key: SeedKey, generation: number) => {
        // locked since the unlock began: the key does not go to the worker
        if (generation !== lockGeneration) return;
        let started: Promise<boolean>;
        try {
            started = engine.startSessionSigner({
                mnemonic: key.mnemonic,
                address: key.address,
                userId: key.userId,
                isCurrent: () => generation === lockGeneration,
            });
        } catch (error) {
            logFailure('sessionSeed', errorName(error));
            return;
        }
        started.catch((error: unknown) =>
            logFailure('sessionSeed', errorName(error)),
        );
    };

    /** Attaches to the session signer when it holds this browser's envelope (no prompt), then decides the signer. */
    const attachSession = async (engine: Engine): Promise<void> => {
        const generation = lockGeneration;
        try {
            await engine.attachSessionIfMatching(
                () => generation === lockGeneration,
            );
        } catch (error) {
            logFailure('sessionAttach', errorName(error));
        }
    };

    /**
     * Decide the signer from the stored key, this tab's memory and the account. Keeps the same signer object while the
     * address is unchanged (hooks memoize on it). The engine is bound before a signer is first handed out. A stored key
     * that no longer holds the wallet the caches describe retires that wallet first (`retireSigner`), so the previous
     * wallet's signer and data are never next to the new key. Sealed, legacy or held back for a protect step: no
     * signer.
     */
    const refreshSigner = async (): Promise<void> => {
        let epoch = teardownEpoch;
        const engine = await loadEngine();
        if (epoch !== teardownEpoch) return;
        if (wouldChangeWallet(heldAddressOf(engine.readKeyEnvelope()))) {
            epoch = await retireSigner('switch');
            if (epoch !== teardownEpoch) return;
        }
        const fields = lockFields(engine);
        const held = fields.address;
        const record = engine.readKeyRecord();
        const { user, userLoaded, signer } = get();
        const usable =
            record !== null &&
            held !== null &&
            !fields.sealed &&
            !fields.needsProtection &&
            sameAddress(record.address, held) &&
            !holdForProtect(held);
        const unlocked = usable && (!userLoaded || isListed(user, held));
        if (!record || !unlocked) {
            set({ ...fields, signer: null });
            return;
        }
        if (signer && sameAddress(signer.address, record.address)) {
            set(fields);
            return;
        }
        await ensureEngineReady(engine);
        if (epoch !== teardownEpoch) return;
        const current = engine.readKeyRecord();
        if (!current || !sameAddress(current.address, record.address)) return;
        let account: unknown;
        try {
            // the shim's signer itself: the key stays in its closures
            account = await engine.readMasterAccount('');
        } catch (error) {
            logFailure('signer', errorName(error));
            set({ ...fields, signer: null });
            return;
        }
        if (epoch !== teardownEpoch) return;
        if (engine.readKeyRecord() === null) {
            // locked while the signer was read: neither tier holds the wallet any more
            set({ ...lockFields(engine), signer: null });
            return;
        }
        servedAddress = current.address;
        set({
            ...lockFields(engine),
            signer: { address: current.address, account },
        });
    };

    /**
     * A legacy clear record (`v: 1`) found in storage: loaded into memory and the protect view opened at once, not
     * dismissable. Its signer is published only once the envelope replaced it. False when there is none to adopt.
     */
    const adoptLegacy = (engine: Engine): boolean => {
        const record = engine.readLegacyKeyRecord();
        if (!record) return false;
        if (protectPending) {
            return (
                protectPending.for === 'legacy' &&
                sameAddress(protectPending.address, record.address)
            );
        }
        let mnemonic: string;
        try {
            mnemonic = engine.normalizeMnemonic(record.mnemonic);
            const account = engine.accountFromMnemonic(mnemonic);
            ownWrite(() => engine.unlockKeyRecord(account, record.userId));
        } catch (error) {
            // an unusable phrase: the record stays held and locked; forgetting it is the way out
            logFailure('legacy', errorName(error));
            return false;
        }
        // the phrase waits (module-private) only until the protect step seals it
        beginProtect(
            { mnemonic, address: record.address, userId: record.userId },
            'legacy',
        );
        return true;
    };

    const fetchUser = async (): Promise<void> => {
        if (userInflight) return userInflight;
        const epoch = teardownEpoch;
        set({ loadingUser: true });
        const run = async () => {
            try {
                const user = await fetchSnappyUser();
                // signed out meanwhile: this account is no longer the browser's
                if (epoch !== teardownEpoch) return;
                set({ user, userLoaded: true, userError: null });
            } catch (error) {
                if (epoch !== teardownEpoch) return;
                if (isSnappySessionExpiredError(error)) {
                    set({
                        user: null,
                        userLoaded: false,
                        userError: 'expired',
                    });
                    return;
                }
                logFailure('fetchSnappyUser', error);
                // the last good answer stays: a blip must not read as "no backups"
                set({ userError: 'network' });
            } finally {
                if (userInflight === inflight) {
                    userInflight = null;
                    set({ loadingUser: false });
                }
            }
        };
        const inflight = run();
        userInflight = inflight;
        return inflight;
    };

    const applyWrittenBackups = (written: SnappyPutBackupResult) => {
        const { user } = get();
        if (!user) return;
        set({
            user: {
                ...user,
                backups: written.backups,
                backupHistory: written.backupHistory,
            },
        });
    };

    /**
     * Write the pending wallet as this browser's key (replacing any other in one write), sealed by the protect step,
     * which this awaits (the dialog stays locked on it). Over another wallet's key (the "replaceHeld" confirm leads
     * here) that wallet is retired first: locked, its caches cleared, the locked state rendered. A wallet already
     * sealed here (the held one backed up again, or imported) is only opened in memory. Null when a teardown, the end
     * of the setup or a cancelled protect step overtook it; nothing is written then and the held wallet is decided
     * again.
     */
    const finalizeKey = async (engine: Engine): Promise<Hex | null> => {
        if (!pending) return null;
        const { mnemonic, address } = pending;
        const generation = lockGeneration;
        const stored = engine.readKeyEnvelope();
        if (replacesWallet(address, heldAddressOf(stored))) {
            const epoch = await retireSigner('replace');
            if (
                epoch !== teardownEpoch ||
                !pending ||
                !sameAddress(pending.address, address)
            ) {
                // the held key stays (a forget wiped it, or the setup was dropped): unlock it again if it may be
                if (epoch === teardownEpoch) void refreshSigner();
                return null;
            }
        }
        const current = engine.readKeyEnvelope();
        if (
            current !== null &&
            !('legacy' in current) &&
            sameAddress(current.address, address)
        ) {
            // this very wallet is sealed here already: the phrase in hand opens it for this tab (unless a lock came
            // meanwhile: it stays sealed, and the worker is not seeded)
            if (generation === lockGeneration) {
                const account = engine.accountFromMnemonic(mnemonic);
                ownWrite(() => engine.unlockKeyRecord(account, current.userId));
                seedSession(
                    engine,
                    { mnemonic, address, userId: current.userId },
                    generation,
                );
            }
        } else {
            const epoch = teardownEpoch;
            const sealed = await new Promise<boolean>((resolve) => {
                beginProtect(
                    { mnemonic, address, userId: get().user?.userId ?? null },
                    'setup',
                );
                protectWaiter = resolve;
                // the backup is done: the protect view shows its own progress
                set({ saving: false });
            });
            if (!sealed) {
                // cancelled (the held key, retired for it, is decided again) or overtaken by a teardown
                if (epoch === teardownEpoch) void refreshSigner();
                return null;
            }
            if (
                epoch !== teardownEpoch ||
                !pending ||
                !sameAddress(pending.address, address)
            ) {
                return null;
            }
        }
        replaceHeldConfirmed = false;
        set({ address });
        return address;
    };

    const setupView = (kind: BrowserWalletSetupKind): BrowserWalletView =>
        pending !== null || kind === 'backup' ? 'backup' : kind;

    const continueSetup = (): void => {
        const kind = get().pendingSetup;
        const { user, userLoaded, address } = get();
        if (!kind || !userLoaded) return;
        const backups = user?.backups ?? [];
        const needsSlot =
            kind === 'create' ||
            (kind === 'backup' &&
                pending !== null &&
                !isListed(user, pending.address));
        if (needsSlot && backups.length >= MAX_BACKUPS) {
            endSetup();
            set({ error: SETUP_COPY.cap, view: 'backups' });
            return;
        }
        if (kind === 'create' && address && !replaceHeldConfirmed) {
            set({ view: 'replaceHeld' });
            return;
        }
        if (!get().termsAccepted) {
            set({ view: 'terms' });
            return;
        }
        set({ view: setupView(kind) });
    };

    /** Snappy still hands out a readable backup of `wallet`. Any failure (a 404 `NO_BACKUP` included) is a no. */
    const backupReadable = async (
        engine: Engine,
        wallet: Hex,
    ): Promise<boolean> => {
        try {
            // any failure means "upload it": no retries on the way
            const response = await getBackupBlob(wallet, { retry: 0 });
            return sameAddress(
                engine.parseBackupValue(response.blob).address,
                wallet,
            );
        } catch (error) {
            if (!isSnappyApiError(error) || error.code !== 'NO_BACKUP') {
                logFailure('settleImport', error);
            }
            return false;
        }
    };

    /**
     * Where an imported phrase goes next: the held-key confirm for another wallet; straight in when Snappy hands out a
     * readable backup of it (the list can be stale either way); else the backup form (refused at the cap).
     */
    const settleImport = async (): Promise<void> => {
        if (!pending) return;
        const wallet = pending.address;
        const held = get().address;
        if (held && !sameAddress(held, wallet) && !replaceHeldConfirmed) {
            set({ view: 'replaceHeld' });
            return;
        }
        const epoch = teardownEpoch;
        const engine = await loadEngine();
        const readable = await backupReadable(engine, wallet);
        // signed out, cancelled or another phrase meanwhile
        if (
            epoch !== teardownEpoch ||
            !pending ||
            !sameAddress(pending.address, wallet)
        ) {
            return;
        }
        if (readable) {
            if (!(await finalizeKey(engine))) return;
            endSetup();
            await fetchUser();
            await refreshSigner();
            set({ view: 'done' });
            toast.success(TOAST_COPY.imported);
            return;
        }
        const { user } = get();
        if (
            !isListed(user, wallet) &&
            (user?.backups.length ?? 0) >= MAX_BACKUPS
        ) {
            endSetup();
            set({ error: SETUP_COPY.cap, view: 'backups' });
            return;
        }
        set({ view: 'backup' });
    };

    const saveFailed = (error: unknown, kind: BrowserWalletSetupKind) => {
        if (isSnappySessionExpiredError(error)) {
            expireSession(SAVE_COPY.expired[kind]);
            return;
        }
        const status = isSnappyApiError(error) ? error.status : null;
        const code = isSnappyApiError(error) ? error.code : null;
        if (status === 403 && code === 'VOUCHER_REQUIRED') {
            // the encrypted blob waits for the code; the same upload is retried after it
            set({
                view: 'voucher',
                error: VOUCHER_COPY.needed,
                voucherAccepted: false,
            });
            return;
        }
        // the encrypted blob stays: a Save from the form encrypts afresh, and after an accepted launch code the
        // voucher view's "Try again" sends this same blob (whose recovery key the user already saved)
        if (status === 409 && code === 'BACKUP_LIMIT') {
            set({ error: SAVE_COPY.limit });
        } else if (status === 409 && code === 'BACKUP_OCCUPIED') {
            set({ error: SAVE_COPY.retry });
        } else if (status === 413) {
            set({ error: SAVE_COPY.tooLarge });
        } else if (status === 400 && code === 'BACKUP_INVALID') {
            set({ error: SAVE_COPY.invalid });
        } else {
            set({ error: SAVE_COPY.failed });
            // name, status and code only: an encryption error must not echo secrets
            logFailure('saveBackup', error);
        }
    };

    /**
     * Upload `pendingBlob`, read it back and compare; only a matching read-back writes the key. Throws the request's
     * error; false when a teardown overtook it or the read-back differs.
     */
    const uploadPending = async (engine: Engine): Promise<boolean> => {
        const blob = pendingBlob;
        const wallet = pending?.address;
        const kind = get().pendingSetup ?? 'create';
        if (!blob || !wallet) return false;
        let epoch = teardownEpoch;
        const written = await putBackup(blob);
        if (epoch !== teardownEpoch) return false;
        applyWrittenBackups(written);
        const readBack = await getBackupBlob(blob.address);
        if (epoch !== teardownEpoch) return false;
        let stored: BackupBlob | null;
        try {
            stored = engine.parseBackupValue(readBack.blob);
        } catch {
            stored = null;
        }
        if (!stored || !engine.sameBackup(stored, blob)) {
            set({ error: SAVE_COPY.readBack });
            return false;
        }
        // the dialog locks while saving, so only a teardown drops the wallet
        if (!pending || !sameAddress(pending.address, wallet)) return false;
        if (!(await finalizeKey(engine))) return false;
        // finalizeKey's retire bumps the epoch when it replaced another wallet
        epoch = teardownEpoch;
        endSetup();
        // a restore blob loaded earlier for this wallet is stale now
        const loaded = get().blob;
        if (loaded && sameAddress(loaded.address, wallet)) set({ blob: null });
        await fetchUser();
        if (epoch !== teardownEpoch) return false;
        await refreshSigner();
        set({ view: 'done', error: null });
        toast.success(SAVED_TOAST[kind], {
            description: TOAST_COPY.backedUpDetail,
        });
        return true;
    };

    const discardRestore = (): null => {
        set({ dialogOpen: false });
        return null;
    };

    const openDialog = async (intent: BrowserWalletIntent): Promise<void> => {
        // an unsaved wallet, or a key waiting for its PIN or passkey, resumes where it was
        if ((pending || protectPending) && onPendingView()) {
            set({ dialogOpen: true });
            return;
        }
        set({
            dialogOpen: true,
            view: 'backups',
            error: null,
            restoreError: null,
            refreshingOnOpen: true,
        });
        const epoch = teardownEpoch;
        try {
            // another device may have added or deleted a backup
            await fetchUser();
            await refreshSigner();
        } catch (error) {
            logFailure('open', error);
        } finally {
            set({ refreshingOnOpen: false });
        }
        if (epoch !== teardownEpoch || !get().dialogOpen) return;
        if (protectPending?.for === 'legacy') {
            set({ view: 'protect' });
            return;
        }
        const { userLoaded, user, address, sealed } = get();
        // a sealed wallet opens first: it is the held identity, and the backup of it needs its phrase
        if (
            sealed &&
            (intent === 'restore' ||
                intent === 'create' ||
                intent === 'backup' ||
                intent === 'unlock')
        ) {
            showUnlock(intent === 'backup' ? 'backup' : null);
            return;
        }
        if (!userLoaded) return;
        if (intent === 'create') {
            // a held key that is not this account's: the backups view says what to do with it first
            if (!address) await get().startSetup('create');
        } else if (intent === 'import') {
            await get().startSetup('import');
        } else if (intent === 'backup') {
            await get().backUpHeld();
        } else if (intent === 'restore') {
            // with a key held, the list: a restore would overwrite it unasked
            const backups = user?.backups ?? [];
            const only = backups.length === 1 ? backups[0] : undefined;
            if (only && !address) await get().selectBackup(only.address);
        }
    };

    /**
     * The envelope to commit for the protect step's key: written (with the memory record) in one write, replacing
     * whatever was stored (a legacy clear record included), then the step's own ending.
     */
    const commitProtected = async (
        engine: Engine,
        key: ProtectPending,
        envelope: Parameters<Engine['storeKeyRecord']>[0],
        generation: number,
    ): Promise<boolean> => {
        if (protectPending !== key || key.epoch !== teardownEpoch) return false;
        // a lock while the PIN or passkey sealed it: the envelope is still written (the wallet is not lost), but
        // sealed here at once, and the worker is not seeded
        const locked = generation !== lockGeneration;
        try {
            // memory keeps the signer only: the phrase leaves with this protect step
            const account = engine.accountFromMnemonic(key.mnemonic);
            ownWrite(() => {
                engine.storeKeyRecord(envelope, account, key.userId);
                if (locked) engine.sealKeyRecord();
            });
        } catch (error) {
            logFailure('storeKey', errorName(error));
            set({ protectError: PROTECT_COPY.failed });
            return false;
        }
        // the new envelope's wallet, for the app's other tabs (a switch re-seeds with the new fingerprint)
        seedSession(engine, key, generation);
        endProtect(true);
        if (key.for === 'restore') {
            set({ address: key.address });
            await refreshSigner();
            set({
                dialogOpen: false,
                view: 'backups',
                selectedBackup: null,
                blob: null,
            });
            toast.success(TOAST_COPY.restored, {
                description: `Trading from ${shortenAddress(key.address)}.`,
            });
        } else if (key.for === 'legacy') {
            await refreshSigner();
            set({ dialogOpen: false, view: 'backups' });
            toast.success(TOAST_COPY.protected);
        } else if (key.for === 'switch') {
            await refreshSigner();
            set({ view: 'backups' });
            toast.success(TOAST_COPY.lockChanged);
        }
        // a setup: finalizeKey goes on from here (its own "done" view and toast)
        return true;
    };

    /**
     * Backing up the held wallet again, from the phrase a confirm just opened from its envelope (never from memory):
     * the phrase becomes the pending wallet of a 'backup' setup until its upload is confirmed.
     */
    const startHeldBackup = (mnemonic: string, address: Hex) => {
        if (!get().userLoaded) {
            set({ view: 'backups' });
            return;
        }
        endSetup();
        set({ pendingSetup: 'backup', error: null, view: 'backups' });
        setPending(mnemonic, address);
        continueSetup();
    };

    /**
     * An opened envelope (an unlock, or a confirm of the current PIN or passkey): a sealed tab keeps its signer (the
     * phrase is not kept), then the phrase goes only to the continuation that asked for it (a protect request for a
     * switch, a backup) and is dropped with it. Nothing continues when the envelope changed meanwhile.
     */
    const finishUnlock = async (
        engine: Engine,
        envelope: Exclude<StoredView, null>,
        mnemonic: string,
        generation: number,
    ): Promise<boolean> => {
        // locked while the PIN or passkey was being checked: the phrase is dropped, the tab stays sealed
        if (generation !== lockGeneration) return false;
        const current = engine.readKeyEnvelope();
        if (
            'legacy' in envelope ||
            current === null ||
            'legacy' in current ||
            current.ct !== envelope.ct
        ) {
            // replaced or wiped while it was opened
            await refreshSigner();
            return false;
        }
        if (!engine.hasLocalSigner()) {
            const account = engine.accountFromMnemonic(mnemonic);
            ownWrite(() => engine.unlockKeyRecord(account, envelope.userId));
            await refreshSigner();
            // locked during the refresh: the lock sealed this tab again; nothing continues, nothing is seeded
            if (generation !== lockGeneration) return false;
        }
        // every tab-local unlock (re)seeds the session signer: a fresh 12 h, this build's worker, this envelope
        seedSession(
            engine,
            {
                mnemonic,
                address: envelope.address,
                userId: envelope.userId,
            },
            generation,
        );
        const then = get().unlockThen;
        set({ unlockThen: null, unlockError: null });
        // a signature that waited for this tab's own signer goes on
        resolveSignWaiters();
        if (then === 'pin' || then === 'passkey') {
            beginProtect(
                {
                    mnemonic,
                    address: envelope.address,
                    userId: envelope.userId,
                },
                'switch',
                then,
            );
        } else if (then === 'backup') {
            startHeldBackup(mnemonic, envelope.address);
        } else {
            set({ dialogOpen: false, view: 'backups' });
        }
        return true;
    };

    /** The wrong-PIN throttle (§7 R4): null when a PIN may be tried now, else the copy to show. */
    const pinThrottle = (): string | null => {
        const wait = pinRetryAtMs - Date.now();
        if (wait <= 0) return null;
        set({ pinRetryAt: pinRetryAtMs });
        return UNLOCK_COPY.retryIn(Math.ceil(wait / 1000));
    };

    const pinFailed = () => {
        pinFailures += 1;
        if (pinFailures >= PIN_FREE_ATTEMPTS) {
            const seconds = Math.min(
                2 ** (pinFailures - PIN_FREE_ATTEMPTS),
                PIN_MAX_WAIT_S,
            );
            pinRetryAtMs = Date.now() + seconds * 1000;
            set({ pinRetryAt: pinRetryAtMs });
        }
    };

    const pinSucceeded = () => {
        pinFailures = 0;
        pinRetryAtMs = 0;
        if (get().pinRetryAt !== null) set({ pinRetryAt: null });
    };

    onForeignKeyChange = (engine) => {
        const stored = engine.readKeyEnvelope();
        const next = heldAddressOf(stored);
        const held = get().address;
        if (next && held && sameAddress(next, held)) {
            // the same wallet re-sealed in another tab (another method), or its legacy record protected there: this
            // tab keeps its memory record (the shim did)
            if (
                protectPending?.for === 'legacy' &&
                stored &&
                !('legacy' in stored)
            ) {
                endProtect(false);
                set({ dialogOpen: false, view: 'backups' });
            }
            void refreshSigner();
            return;
        }
        // wiped or replaced in another tab: a lock for whatever unlock or seed of the old wallet is still in flight
        lockGeneration += 1;
        // whatever protect step was open was about the wallet that just changed
        abandonProtect();
        if (!held && servedWallet() === null) {
            // nothing held here: no wallet to retire
            set({ ...lockFields(engine), signer: null });
            if (stored) {
                adoptLegacy(engine);
                void refreshSigner();
            }
            return;
        }
        // wiped or replaced in another tab: the old wallet is retired (whatever was keyed to it drops its result, its
        // caches go) and the locked state renders before the new key is decided
        const retired = retireSigner('foreign');
        set(lockFields(engine));
        if (stored) {
            void retired.then(async () => {
                adoptLegacy(engine);
                await refreshSigner();
            });
        }
    };

    onSessionChange = () => {
        // the same wallet, attached to or detached from the session signer: decide the signer and the tiers again
        void refreshSigner();
    };

    onSessionMessage = (engine, message) => {
        const type =
            typeof message === 'object' && message !== null
                ? (message as { type?: unknown }).type
                : null;
        if (type === 'lock') {
            // "Lock on all tabs" in another tab: an unlock or a seed in flight here drops its result
            lockGeneration += 1;
            // the sender forgot ITS build's worker; this tab may run another build, whose worker only this tab's
            // forget reaches (unconditionally: whatever that worker holds goes)
            engine
                .forgetSession()
                .catch((error: unknown) =>
                    logFailure('sessionForget', errorName(error)),
                );
            // then this tab seals its own
            ownWrite(() => {
                engine.detachSessionTier();
                engine.sealKeyRecord();
            });
            void refreshSigner();
        } else if (type === 'seeded') {
            // another tab unlocked: a sealed tab of the same envelope unlocks with no prompt
            void attachSession(engine).then(refreshSigner);
        }
    };

    const initialProtection = peekHeldProtection();

    return {
        address: peekHeldAddress(),
        sealed: initialProtection === 'pin' || initialProtection === 'passkey',
        protection:
            initialProtection === 'pin' || initialProtection === 'passkey'
                ? initialProtection
                : null,
        needsProtection: initialProtection === 'legacy',
        sessionUnlocked: false,
        localUnlocked: false,
        passkeyAvailable: false,
        protectFor: null,
        protectTarget: null,
        protecting: false,
        protectError: null,
        passkeyFailed: false,
        unlocking: false,
        unlockError: null,
        unlockThen: null,
        pinRetryAt: null,
        signer: null,
        user: null,
        userLoaded: false,
        userError: null,
        loadingUser: false,
        booted: false,

        dialogOpen: false,
        view: 'backups',
        refreshingOnOpen: false,
        error: null,
        selectedBackup: null,
        blob: null,
        loadingBlob: false,
        blobError: null,
        restoring: false,
        restoreError: null,
        saving: false,
        redeeming: false,
        voucherAccepted: false,
        deleting: null,
        termsAccepted: readTermsAccepted(),
        pendingSetup: null,
        importTarget: null,
        pendingWords: [],
        pendingAddress: null,

        boot: async () => {
            if (bootInflight) return bootInflight;
            const run = async () => {
                try {
                    const engine = await loadEngine();
                    const held = heldAddressOf(engine.readKeyEnvelope());
                    // a key that is not the wallet this tab trades as: retired before its address is published
                    if (wouldChangeWallet(held)) await retireSigner('switch');
                    set(lockFields(engine));
                    // a legacy clear record: into memory and the protect view, before anything else
                    adoptLegacy(engine);
                    // the session signer of another open tab may hold this envelope: unlocked with no prompt
                    await Promise.all([fetchUser(), attachSession(engine)]);
                    await refreshSigner();
                } catch (error) {
                    logFailure('boot', error);
                } finally {
                    if (bootInflight === inflight) bootInflight = null;
                    set({ booted: true });
                }
            };
            const inflight = run();
            bootInflight = inflight;
            return inflight;
        },

        open: (intent) => {
            if (intent === 'unlock') {
                get().openUnlock();
                return;
            }
            void openDialog(intent);
        },

        openUnlock: () => {
            if (protectPending?.for === 'legacy') {
                set({ dialogOpen: true, view: 'protect' });
                return;
            }
            if (get().sealed || get().needsProtection) {
                // a legacy record that could not be loaded shows the unlock view's forget line only
                showUnlock(get().unlockThen);
                return;
            }
            // not known to be sealed (yet): decided after the refresh
            void openDialog('unlock');
        },

        unlockWithPin: async (pin) => {
            if (get().unlocking) return false;
            if (!PIN_PATTERN.test(pin)) {
                set({ unlockError: UNLOCK_COPY.wrongPin });
                return false;
            }
            // the throttle is checked before any scrypt runs
            const throttled = pinThrottle();
            if (throttled) {
                set({ unlockError: throttled });
                return false;
            }
            set({ unlocking: true, unlockError: null });
            const epoch = teardownEpoch;
            // a lock from here on wins: the opened phrase is dropped and the tab stays sealed
            const generation = lockGeneration;
            const stale = () =>
                epoch !== teardownEpoch || generation !== lockGeneration;
            try {
                await yieldToPaint();
                if (stale()) return false;
                const engine = await loadEngine();
                if (stale()) return false;
                const stored = engine.readKeyEnvelope();
                if (!stored || 'legacy' in stored || stored.method !== 'pin') {
                    await refreshSigner();
                    return false;
                }
                const mnemonic = await engine.openWithPin(stored, pin);
                pinSucceeded();
                if (stale()) return false;
                return await finishUnlock(engine, stored, mnemonic, generation);
            } catch (error) {
                if (errorName(error) === 'VaultPinError') pinFailed();
                if (stale()) return false;
                const copy = unlockErrorCopy(error, 'pin');
                if (copy !== UNLOCK_COPY.wrongPin)
                    logFailure('unlockWithPin', errorName(error));
                set({ unlockError: copy });
                return false;
            } finally {
                set({ unlocking: false });
            }
        },

        unlockWithPasskey: async () => {
            if (get().unlocking) return false;
            set({ unlocking: true, unlockError: null });
            const epoch = teardownEpoch;
            // a lock from here on wins: the PRF secret is zeroed unused and the tab stays sealed
            const generation = lockGeneration;
            const stale = () =>
                epoch !== teardownEpoch || generation !== lockGeneration;
            try {
                // no await before the prompt when the engine is loaded: WebAuthn wants the click's activation
                const engine = engineModule ?? (await loadEngine());
                if (stale()) return false;
                const stored = engine.readKeyEnvelope();
                if (
                    !stored ||
                    'legacy' in stored ||
                    stored.method !== 'passkey' ||
                    !stored.passkey
                ) {
                    await refreshSigner();
                    return false;
                }
                const secret = await engine.assertPrf(stored.passkey);
                let mnemonic: string;
                try {
                    if (stale()) return false;
                    mnemonic = engine.openWithSecret(stored, secret);
                } finally {
                    secret.fill(0);
                }
                return await finishUnlock(engine, stored, mnemonic, generation);
            } catch (error) {
                if (stale()) return false;
                logFailure('unlockWithPasskey', errorName(error));
                set({ unlockError: unlockErrorCopy(error, 'passkey') });
                return false;
            } finally {
                set({ unlocking: false });
            }
        },

        setupPin: async (pin) => {
            const key = protectPending;
            if (get().protecting || !key) return false;
            if (!PIN_PATTERN.test(pin)) {
                set({ protectError: PROTECT_COPY.pinFormat });
                return false;
            }
            set({ protecting: true, protectError: null });
            const generation = lockGeneration;
            try {
                await yieldToPaint();
                const engine = await loadEngine();
                const kdf = await engine.calibratePinKdf();
                const envelope = await engine.sealWithPin(
                    {
                        mnemonic: key.mnemonic,
                        address: key.address,
                        userId: key.userId,
                    },
                    pin,
                    kdf,
                );
                return await commitProtected(engine, key, envelope, generation);
            } catch (error) {
                if (protectPending !== key) return false;
                logFailure('setupPin', errorName(error));
                set({ protectError: PROTECT_COPY.failed });
                return false;
            } finally {
                set({ protecting: false });
            }
        },

        setupPasskey: async () => {
            const key = protectPending;
            if (get().protecting || !key) return false;
            set({ protecting: true, protectError: null, passkeyFailed: false });
            const generation = lockGeneration;
            try {
                // no await before the prompt when the engine is loaded: WebAuthn wants the click's activation
                const engine = engineModule ?? (await loadEngine());
                const { secret, ...passkey } = await engine.registerPrfPasskey({
                    address: key.address,
                });
                let envelope: Parameters<Engine['storeKeyRecord']>[0];
                try {
                    envelope = engine.sealWithSecret(
                        {
                            mnemonic: key.mnemonic,
                            address: key.address,
                            userId: key.userId,
                        },
                        secret,
                        passkey,
                    );
                } finally {
                    secret.fill(0);
                }
                return await commitProtected(engine, key, envelope, generation);
            } catch (error) {
                if (protectPending !== key) return false;
                logFailure('setupPasskey', errorName(error));
                set({
                    passkeyFailed: true,
                    protectError: passkeyErrorCopy(error),
                });
                return false;
            } finally {
                set({ protecting: false });
            }
        },

        cancelProtect: () => {
            const key = protectPending;
            if (!key || key.for === 'legacy' || get().protecting) return;
            endProtect(false);
            set({ view: 'backups', error: null, restoreError: null });
            if (key.for === 'setup') {
                // finalizeKey decides the held wallet again
                endSetup();
            } else if (key.for === 'restore') {
                // the held wallet, retired for the restore, is decided again
                void refreshSigner();
            }
        },

        changeProtection: async (target) => {
            const { protecting, unlocking, address, needsProtection } = get();
            if (protecting || unlocking || !address || needsProtection) return;
            // the current PIN or passkey first, even in an unlocked tab: the new lock is made from the envelope it
            // opens, never from memory (an injected script cannot re-seal the key under a PIN it chose)
            showUnlock(target);
        },

        changePin: async () => get().changeProtection('pin'),

        refreshUser: async () => {
            await fetchUser();
            await refreshSigner();
        },

        dismiss: () => {
            const keep =
                (pending !== null || protectPending !== null) &&
                onPendingView();
            set({ dialogOpen: false, unlockThen: null, unlockError: null });
            // a signature that waited for this tab's unlock is cancelled
            rejectSignWaiters();
            if (!keep) {
                endSetup();
                set({ view: 'backups', error: null, restoreError: null });
            }
        },

        showBackups: () => {
            endSetup();
            rejectSignWaiters();
            set({
                view: 'backups',
                error: null,
                restoreError: null,
                unlockThen: null,
                unlockError: null,
            });
        },

        selectBackup: async (address) => {
            const { userLoaded, user } = get();
            const row = user?.backups.find((backup) =>
                sameAddress(backup.address, address),
            );
            if (!userLoaded || !row) return;
            set({
                selectedBackup: row,
                restoreError: null,
                error: null,
                view: 'restore',
            });
            await get().loadBlob(row.address);
        },

        loadBlob: async (address) => {
            const loaded = get().blob;
            if (
                loaded &&
                !get().blobError &&
                sameAddress(loaded.address, address)
            ) {
                return;
            }
            const request = ++blobRequest;
            const epoch = teardownEpoch;
            const stale = () =>
                epoch !== teardownEpoch || request !== blobRequest;
            set({ loadingBlob: true, blobError: null, blob: null });
            try {
                const response = await getBackupBlob(address);
                const engine = await loadEngine();
                if (stale()) return;
                let parsed: BackupBlob;
                try {
                    parsed = engine.parseBackupValue(response.blob);
                } catch {
                    set({
                        blobError: {
                            code: 'other',
                            message: BLOB_COPY.unreadable,
                        },
                    });
                    return;
                }
                set({ blob: parsed });
            } catch (error) {
                // a teardown's 401 is not an expired sign-in to report
                if (stale()) return;
                if (isSnappySessionExpiredError(error)) {
                    set({
                        userError: 'expired',
                        blobError: {
                            code: 'expired',
                            message: BLOB_COPY.expired,
                        },
                    });
                } else if (
                    isSnappyApiError(error) &&
                    error.code === 'NO_BACKUP'
                ) {
                    set({
                        blobError: {
                            code: 'other',
                            message: BLOB_COPY.noBackup,
                        },
                    });
                } else if (isSnappyApiError(error) && error.status === 429) {
                    set({
                        blobError: {
                            code: 'other',
                            message: BLOB_COPY.rateLimited,
                        },
                    });
                } else {
                    logFailure('loadBlob', error);
                    set({
                        blobError: { code: 'other', message: BLOB_COPY.failed },
                    });
                }
            } finally {
                if (request === blobRequest) set({ loadingBlob: false });
            }
        },

        restore: async (password, recoveryKey) => {
            if (get().restoring) return null;
            const blob = get().blob;
            if (!blob) {
                set({ restoreError: RESTORE_COPY.failed });
                return null;
            }
            const expected = get().selectedBackup?.address;
            if (expected && !sameAddress(expected, blob.address)) {
                set({ restoreError: RESTORE_COPY.addressMismatch });
                return null;
            }
            const needsRecoveryKey = blob.v === 3;
            if (needsRecoveryKey && recoveryKey.trim() === '') {
                set({ restoreError: RESTORE_COPY.needsRecoveryKey });
                return null;
            }
            set({ restoring: true, restoreError: null });
            let epoch = teardownEpoch;
            let retired = false;
            try {
                await yieldToPaint();
                const engine = await loadEngine();
                // the phrase lives in this frame and the protect step only: it is sealed and goes nowhere else
                const mnemonic = await engine.decryptBackup(
                    blob,
                    password,
                    needsRecoveryKey ? recoveryKey : undefined,
                );
                // signed out or forgotten meanwhile: the key must not come back
                if (epoch !== teardownEpoch) return discardRestore();
                const restored = engine.masterAddress(mnemonic);
                const held = heldAddressOf(engine.readKeyEnvelope());
                // replacing another wallet's key: that wallet is retired (an action still keyed to it drops its
                // result, its caches go, the locked state renders) before the protect step, which overwrites it
                if (replacesWallet(restored, held)) {
                    epoch = await retireSigner('restore');
                    retired = true;
                    if (epoch !== teardownEpoch) return discardRestore();
                }
                // nothing is written yet: the protect step seals the key (and toasts), a cancel drops it
                beginProtect(
                    {
                        mnemonic,
                        address: restored,
                        userId: get().user?.userId ?? null,
                    },
                    'restore',
                );
                return restored;
            } catch (error) {
                if (epoch !== teardownEpoch) return discardRestore();
                const copy = restoreErrorCopy(error, needsRecoveryKey);
                // the error name only: an unexpected error's text could carry secrets
                if (copy === RESTORE_COPY.failed)
                    logFailure('restore', errorName(error));
                set({ restoreError: copy });
                // failed after the held wallet was retired: whatever key the record holds is decided again
                if (retired) void refreshSigner();
                return null;
            } finally {
                set({ restoring: false });
            }
        },

        startSetup: async (kind, options = {}) => {
            if (!get().userLoaded) return;
            endSetup();
            set({
                pendingSetup: kind,
                importTarget:
                    kind === 'import' ? (options.target ?? null) : null,
                error: null,
            });
            continueSetup();
        },

        backUpHeld: async () => {
            const { userLoaded, address, needsProtection } = get();
            if (!userLoaded || !address || needsProtection) return;
            // the phrase comes only from the envelope, opened by the current PIN or passkey (confirm mode)
            showUnlock('backup');
        },

        keepHeldWallet: async () => get().backUpHeld(),

        confirmReplaceHeld: async () => {
            if (get().view !== 'replaceHeld') return;
            replaceHeldConfirmed = true;
            // an imported phrase waits on the confirm; a create confirms up front
            if (pending) return settleImport();
            continueSetup();
        },

        cancelReplaceHeld: () => {
            endSetup();
            set({ error: null, view: 'backups' });
        },

        acceptTerms: () => {
            writeTermsAccepted();
            const kind = get().pendingSetup;
            set({
                termsAccepted: true,
                view: kind ? setupView(kind) : 'backups',
            });
        },

        createWallet: async () => {
            const { userLoaded, pendingSetup, view } = get();
            if (!userLoaded || pendingSetup !== 'create' || view !== 'create') {
                return false;
            }
            // a second click must not change words the user may be writing down
            if (pending) return true;
            set({ error: null });
            const epoch = teardownEpoch;
            try {
                const engine = await loadEngine();
                if (
                    epoch !== teardownEpoch ||
                    get().pendingSetup !== 'create'
                ) {
                    return false;
                }
                if (pending) return true;
                const mnemonic = engine.generateMnemonic();
                setPending(mnemonic, engine.masterAddress(mnemonic));
                set({ pendingWords: mnemonic.split(' ') });
                return true;
            } catch (error) {
                if (epoch !== teardownEpoch) return false;
                logFailure('createWallet', errorName(error));
                set({ error: SETUP_COPY.failed });
                return false;
            }
        },

        continueToBackup: () => {
            if (pending && get().view === 'create') {
                // the words are on paper now; they leave state
                set({ view: 'backup', pendingWords: [] });
            }
        },

        importPhrase: async (phrase) => {
            const { userLoaded, pendingSetup, view } = get();
            if (!userLoaded || pendingSetup !== 'import' || view !== 'import') {
                return false;
            }
            set({ error: null });
            const epoch = teardownEpoch;
            let derived: { mnemonic: string; address: Hex } | null;
            try {
                const engine = await loadEngine();
                const mnemonic = engine.normalizeMnemonic(phrase);
                derived = engine.isValidMnemonic(mnemonic)
                    ? { mnemonic, address: engine.masterAddress(mnemonic) }
                    : null;
            } catch (error) {
                if (epoch !== teardownEpoch) return false;
                logFailure('importPhrase', errorName(error));
                set({ error: SETUP_COPY.failed });
                return false;
            }
            if (epoch !== teardownEpoch || get().pendingSetup !== 'import') {
                return false;
            }
            if (!derived) {
                set({ error: SETUP_COPY.invalidPhrase });
                return false;
            }
            const target = get().importTarget;
            if (target && !sameAddress(target, derived.address)) {
                set({ error: SETUP_COPY.otherWallet });
                return false;
            }
            setPending(derived.mnemonic, derived.address);
            await settleImport();
            return true;
        },

        saveBackup: async (password, recoveryKey) => {
            const kind = get().pendingSetup ?? 'create';
            if (get().saving || !pending || get().view !== 'backup')
                return false;
            const { mnemonic } = pending;
            set({ saving: true, error: null });
            const epoch = teardownEpoch;
            try {
                await yieldToPaint();
                const engine = await loadEngine();
                const blob = await engine.encryptBackup(
                    mnemonic,
                    password,
                    recoveryKey,
                );
                if (epoch !== teardownEpoch || !pending) return false;
                rememberBlob(blob);
                return await uploadPending(engine);
            } catch (error) {
                if (epoch !== teardownEpoch) return false;
                saveFailed(error, kind);
                return false;
            } finally {
                set({ saving: false });
            }
        },

        redeemVoucher: async (code) => {
            if (get().redeeming) return false;
            set({ redeeming: true, error: null });
            const epoch = teardownEpoch;
            try {
                let admission: { enabled: boolean; admitted: boolean };
                try {
                    admission = await redeemSnappyAdmission(
                        code.trim().toUpperCase(),
                    );
                } catch (error) {
                    if (epoch !== teardownEpoch) return false;
                    if (isSnappySessionExpiredError(error)) {
                        expireSession();
                        return false;
                    }
                    const known =
                        isSnappyApiError(error) && error.code
                            ? VOUCHER_ERROR_COPY[error.code]
                            : undefined;
                    if (known) {
                        set({ error: known });
                    } else if (
                        isSnappyApiError(error) &&
                        error.status === 429
                    ) {
                        set({ error: VOUCHER_COPY.rateLimited });
                    } else {
                        logFailure('redeemVoucher', error);
                        set({ error: VOUCHER_COPY.failed });
                    }
                    return false;
                }
                if (epoch !== teardownEpoch) return false;
                if (admission.enabled && !admission.admitted) {
                    set({ error: VOUCHER_COPY.failed });
                    return false;
                }
                set({ voucherAccepted: true });
            } finally {
                set({ redeeming: false });
            }
            if (!pending) {
                set({ view: 'backups', voucherAccepted: false });
                return true;
            }
            if (!pendingBlob) {
                set({ view: 'backup', voucherAccepted: false });
                return true;
            }
            await get().retrySave();
            return true;
        },

        retrySave: async () => {
            const kind = get().pendingSetup ?? 'create';
            if (get().saving || !pending || !pendingBlob) return false;
            set({ saving: true, error: null });
            const epoch = teardownEpoch;
            try {
                const engine = await loadEngine();
                return await uploadPending(engine);
            } catch (error) {
                if (epoch !== teardownEpoch) return false;
                saveFailed(error, kind);
                return false;
            } finally {
                set({ saving: false });
            }
        },

        cancelSetup: () => {
            endSetup();
            set({ error: null, view: 'backups' });
        },

        deleteBackup: async (address) => {
            if (get().deleting) return false;
            set({ deleting: address, error: null });
            const epoch = teardownEpoch;
            try {
                await deleteBackupRequest(address);
                if (epoch !== teardownEpoch) return false;
                const loaded = get().blob;
                if (loaded && sameAddress(loaded.address, address)) {
                    set({ blob: null });
                }
                const selected = get().selectedBackup;
                if (selected && sameAddress(selected.address, address)) {
                    set({ selectedBackup: null });
                }
                const { user } = get();
                if (user) {
                    set({
                        user: {
                            ...user,
                            backups: user.backups.filter(
                                (backup) =>
                                    !sameAddress(backup.address, address),
                            ),
                        },
                    });
                }
                await fetchUser();
                if (epoch !== teardownEpoch) return false;
                // a held key of that wallet stays, but it is no longer this account's: locked until backed up again
                await refreshSigner();
                toast.success(TOAST_COPY.deleted);
                return true;
            } catch (error) {
                if (epoch !== teardownEpoch) return false;
                if (isSnappySessionExpiredError(error)) {
                    expireSession();
                    return false;
                }
                logFailure('deleteBackup', error);
                set({ error: DELETE_COPY.failed });
                return false;
            } finally {
                set({ deleting: null });
            }
        },

        clearError: () => set({ error: null }),

        signInAgain: () => {
            endSetup();
            set({ dialogOpen: false, view: 'backups', error: null });
            useSignInDialogStore.getState().open();
        },

        readRecoveryPhrase: async (credential) => {
            const method: BrowserWalletProtection =
                'pin' in credential ? 'pin' : 'passkey';
            let engine: Engine;
            try {
                engine = engineModule ?? (await loadEngine());
            } catch (error) {
                logFailure('readRecoveryPhrase', errorName(error));
                return { error: UNLOCK_COPY.failed };
            }
            const stored = engine.readKeyEnvelope();
            if (!stored || 'legacy' in stored || stored.method !== method) {
                return { error: UNLOCK_COPY.unreadable };
            }
            try {
                let mnemonic: string;
                if ('pin' in credential) {
                    if (!PIN_PATTERN.test(credential.pin)) {
                        return { error: UNLOCK_COPY.wrongPin };
                    }
                    const throttled = pinThrottle();
                    if (throttled) return { error: throttled };
                    await yieldToPaint();
                    mnemonic = await engine.openWithPin(stored, credential.pin);
                    pinSucceeded();
                } else {
                    if (!stored.passkey)
                        return { error: UNLOCK_COPY.unreadable };
                    const secret = await engine.assertPrf(stored.passkey);
                    try {
                        mnemonic = engine.openWithSecret(stored, secret);
                    } finally {
                        secret.fill(0);
                    }
                }
                // handed to the caller only, which shows the words and drops them
                return { words: mnemonic.split(' ') };
            } catch (error) {
                if (errorName(error) === 'VaultPinError') pinFailed();
                const copy = unlockErrorCopy(error, method);
                if (copy !== UNLOCK_COPY.wrongPin)
                    logFailure('readRecoveryPhrase', errorName(error));
                return { error: copy };
            }
        },

        forget: () => {
            teardownEpoch += 1;
            lockGeneration += 1;
            endSetup();
            rejectSignWaiters();
            // the session signer holds this wallet for the other tabs: it goes too (a never loaded engine first loads,
            // when a wallet is held: another tab may have seeded it)
            if (engineModule) {
                void engineModule.forgetSession();
            } else if (get().address !== null) {
                void loadEngine()
                    .then(async (engine) => engine.forgetSession())
                    .catch((error: unknown) =>
                        logFailure('sessionForget', errorName(error)),
                    );
            }
            // a key waiting for its PIN or passkey goes with it (a waiting finalizeKey learns it was not sealed)
            if (protectPending) endProtect(false);
            // locked and the V2 caches cleared before the key goes (synchronously: Sign out reloads right after)
            if (get().address !== null || servedWallet() !== null) {
                void retireSigner('forget');
            }
            wipeKey();
            set({
                address: null,
                signer: null,
                sealed: false,
                sessionUnlocked: false,
                localUnlocked: false,
                protection: null,
                needsProtection: false,
                unlocking: false,
                unlockError: null,
                unlockThen: null,
                dialogOpen: false,
                view: 'backups',
                error: null,
                selectedBackup: null,
                blob: null,
                blobError: null,
                restoreError: null,
            });
        },

        lockEverywhere: async () => {
            // at once, before any await: an unlock or a seed in flight here drops its result
            lockGeneration += 1;
            let engine: Engine;
            try {
                engine = await loadEngine();
            } catch (error) {
                logFailure('lockEverywhere', errorName(error));
                return;
            }
            // and again: an unlock begun while the engine loaded is older than this lock too
            lockGeneration += 1;
            // this build's worker drops the key (detached here synchronously); every other tab forgets its own
            // build's worker and seals its own signer when it hears the lock
            const forgetting = engine.forgetSession();
            engine.announceSession({ type: 'lock' });
            ownWrite(() => {
                engine.detachSessionTier();
                engine.sealKeyRecord();
            });
            rejectSignWaiters();
            await refreshSigner();
            try {
                await forgetting;
            } catch (error) {
                logFailure('lockEverywhere', errorName(error));
            }
            toast.success(TOAST_COPY.lockedEverywhere);
        },

        requestLocalUnlock: async () => {
            const engine = await loadEngine();
            if (engine.hasLocalSigner()) return;
            const { address, needsProtection } = get();
            // no wallet to open, or a step of its own on screen (a new wallet, a protect step) that must not be lost
            if (
                address === null ||
                needsProtection ||
                ((pending !== null || protectPending !== null) &&
                    onPendingView())
            ) {
                throw new engine.WalletLockedError();
            }
            await new Promise<void>((resolve, reject) => {
                signWaiters.push({ resolve, reject });
                const { dialogOpen, view, unlockThen } = get();
                if (!dialogOpen || view !== 'unlock' || unlockThen !== 'sign') {
                    showUnlock('sign');
                }
            });
        },
    };
});

/** For tests: the unbind of the wallet-store binding, when the engine was made ready. */
export function unbindBrowserWalletEngineForTests(): void {
    unbindWalletStore?.();
    unbindWalletStore = null;
    engineReady = null;
}
